import useSentenceSpeaking from "./useSentenceSpeaking";

// 리더스북 따라 말하기 목록(2026-10-17). 지인에듀가 지문 음원을 문장마다 잘라 준다(audio.sentences, version 2).
// 문장 하나를 듣고(▶) 마이크를 눌러 따라 읽으면, 직독직해와 같은 받아쓰기 정확도로 점수를 낸다.
// 듣고 말하는 일은 useSentenceSpeaking 이 한다. 본문 따라 읽기(ReadAloud.jsx)도 같은 것을 쓴다.
//
// - 브라우저가 받아쓰기를 못 하면(파이어폭스, 일부 앱 안 브라우저) 마이크 단추를 감추고 듣기만 둔다.
// - 점수는 첫 시도를 따로 남긴다(calculate_grade 가 첫 시도로 센다). 화면에는 **가장 잘 읽은 점수**를 보인다.
//   다시 읽다가 못 읽으면 좋은 점수가 지워진 것처럼 보였다(2026-10-17 대표).
// - 받아쓴 글은 아이에게 보이지 않는다. 점수만 보인다(대표).

// 마이크 그림. 🎤 는 그림 글꼴이 없는 PC 에서 네모로 보여서 선으로 그린다(해설지 readers_pdf 의 MIC_SVG 와 같은 모양).
export const MicIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4M8 22h8" />
  </svg>
);

export const speakingScoreClass = (accuracy) => {
  if (accuracy >= 90) return "is-great";
  if (accuracy >= 70) return "is-good";
  return "is-low";
};

const SpeakingPanel = ({ audio, speakingMap, onResult, isEditable, onClose }) => {
  const { isSupported, listeningKey, playingKey, play, record } = useSentenceSpeaking({
    sentenceList: audio.sentences,
    onResult: (sentence, accuracy, transcript) => onResult(sentence.idx, accuracy, transcript),
  });
  const listeningIndex = listeningKey;
  const playingIndex = playingKey;

  const shownScoreOf = (spoken) => spoken?.best_accuracy ?? spoken?.last_accuracy;
  const scoreList = audio.sentences
    .map((sentence) => shownScoreOf(speakingMap[sentence.idx]))
    .filter((accuracy) => typeof accuracy === "number");
  const average = scoreList.length ? Math.round(scoreList.reduce((sum, one) => sum + one, 0) / scoreList.length) : null;

  return (
    <div className="speaking">
      <div className="speaking__head">
        <div>
          <div className="speaking__title">한 문장씩 듣고 따라 말하기</div>
          <div className="speaking__sub">
            {scoreList.length}/{audio.sentences.length} 문장
            {average !== null && <span className={`speaking__average ${speakingScoreClass(average)}`}>평균 {average}점</span>}
          </div>
        </div>
        <button type="button" className="speaking__close" onClick={onClose} aria-label="닫기">
          ×
        </button>
      </div>
      <div className="speaking__list">
        {audio.sentences.map((sentence) => {
          const spoken = speakingMap[sentence.idx];
          const isListening = listeningIndex === sentence.idx;
          return (
            <div className={`speaking__row${isListening ? " is-listening" : ""}`} key={sentence.idx}>
              <div className="speaking__text">
                {sentence.speaker && sentence.speaker !== "narrator" && <b>{sentence.speaker}: </b>}
                <span className={sentence.speaker === "narrator" ? "speaking__narration" : ""}>{sentence.text}</span>
              </div>
              <div className="speaking__buttons">
                {sentence.mp3 && (
                  <button
                    type="button"
                    className={`speaking__play${playingIndex === sentence.idx ? " is-playing" : ""}`}
                    onClick={() => play(sentence, sentence.idx)}
                    aria-label="듣기"
                  >
                    ▶
                  </button>
                )}
                {isSupported && isEditable && (
                  <button
                    type="button"
                    className={`speaking__mic${isListening ? " is-listening" : ""}`}
                    onClick={() => record(sentence, sentence.idx)}
                    disabled={listeningIndex !== null && !isListening}
                  >
                    {isListening ? "다 읽었어요" : <MicIcon />}
                  </button>
                )}
                {typeof shownScoreOf(spoken) === "number" && (
                  <span className={`speaking__score ${speakingScoreClass(shownScoreOf(spoken))}`}>
                    {shownScoreOf(spoken)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SpeakingPanel;
